import {
	AEMContent,
	Card,
	Col,
	FieldWrapper,
	Input,
	Paginator,
	PrimaryButton,
	Svg,
	TableBody,
	TableCell,
	TableHead,
	TableHeaderCell,
	Text,
	TextLink,
} from '@mlc/web-ui-toolkit';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { filter } from '@mlc/symbols';
import styled from 'styled-components';
import {
	MemberBenefits as MemberBenefitsDomain,
	MemberBenefitsItem,
} from '../../../../../logic/domains/planProfile.domain';
import {
	fetchMembersInBatches,
	getMemberBenefits,
} from '../../../../../logic/functions/planProfile.function';
import {
	asCurrency,
	asFormattedDate,
	unicodeMinusToMinus,
	asPercentage,
} from '../../../../common/formatter';
import LoadingIndicator from '../../../../common/LoadingIndicator';
import ScrollableTable from '../../../Common/ScrollableTable/ScrollableTable';
import { ZeroMargin } from '../../../Common/styled';
import ApiError from '../../../Error/ApiError';
import DownloadCsv from '../Common/DownloadCsv';
import NoMatching from '../Common/NoMatching';
import { MemberBlocked } from '../Common/StaticContent';
import Tab from '../Common/Tab';
import planProfileTestIds from '../PlanProfile.elements';
import QuickFilter from '../Common/QuickFilter';
import { TableRow } from '../../../SearchResults/AllResultsTabContent/SearchResultsRow/styled';
import * as Styled from './styled';

type Props = {
	planUUID: string;
	planNumber: string;
	planName: string;
	balanceAsOfDate: string;
};

const MemberBenefits: FunctionComponent<Props> = ({
	planUUID,
	planNumber,
	planName,
	balanceAsOfDate,
}) => {
	const [memberBenefits, setMemberBenefits] = useState<MemberBenefitsDomain>();
	const [totalCount, setTotalCount] = useState<number>(0);
	const [isFilter, setIsFilter] = useState(false);
	const [search, setSearch] = useState('');
	const [isInitLoading, setIsInitLoading] = useState(true);
	const [isPageLoading, setIsPageLoading] = useState(false);
	const [paginatorKey, setPaginatorKey] = useState<number>(0);

	const PAGE_SIZE = 25;

	useEffect(() => {
		const fetchData = async () => {
			const memberBenefits = await getMemberBenefits(planUUID, 1, PAGE_SIZE);
			setMemberBenefits(memberBenefits);
			if (memberBenefits) {
				setTotalCount(memberBenefits.totalCount);
			}
			setIsInitLoading(false);
		};
		fetchData();
	}, []);

	const filter = async (phrase: string) => {
		setIsFilter(true);
		phrase = phrase.replace(', ', ' ');
		const memberBenefits = await getMemberBenefits(
			planUUID,
			1,
			PAGE_SIZE,
			phrase
		);
		setSearch(phrase);
		setMemberBenefits(memberBenefits);
		setPaginatorKey(Math.random());
	};

	const updatePage = async (nextStartIndex: number) => {
		const nextPage = nextStartIndex / PAGE_SIZE + 1;
		setIsPageLoading(true);
		setMemberBenefits(
			await getMemberBenefits(planUUID, nextPage, PAGE_SIZE, search)
		);
		setIsPageLoading(false);
	};

	if (isInitLoading) return <LoadingIndicator />;

	if (!memberBenefits) return <ApiError />;

	if (!isFilter && memberBenefits.members.length === 0) return <NoMatching />;

	const members = applyFormatting(memberBenefits.members);

	return (
		<Tab title="Member Benefits">
			<Card justifyContent="space-between" display="flex" alignItems="center">
				<SearchCol col={6}>
					<QuickFilter
						placeholder="Type name or account number"
						fetchData={filter}
					/>
				</SearchCol>
				<DownloadCsv
					options={{
						fileName: `EMP_DETAILS_${planNumber}`,
						title: [`Plan Name,${planName}`, `Plan No.,${planNumber}`],
						fields: CsvFields,
					}}
					fetchDataToDownload={async () =>
						applyFormatting(await fetchMembersInBatches(planUUID, totalCount))
					}
				/>
			</Card>
			<Paginator
				totalItems={memberBenefits.totalCount}
				onPageSelect={updatePage}
				key={paginatorKey}
			>
				{isPageLoading ? (
					<LoadingIndicator />
				) : (
					<ScrollableTable
						topScrollId="memberBenefitsTopScroll"
						containerProps={{
							marginBottom: 4,
							style: { maxHeight: '700px' },
							id: 'memberBenefitsContainer',
						}}
						tableProps={{
							variant: 'bottomLines',
							border: 'mlcBorderThin',
							borderColor: 'mlcAlmostWhite',
							layout: 'auto',
							summary: `Table with 8 columns and ${memberBenefits.totalCount} rows displaying member benefits information`,
							id: 'memberBenefitsTable',
						}}
					>
						<TableHead>
							<Styled.NoWrapTR>
								<Styled.UnalignedHeaderCell scope="col">
									Client name and ID
								</Styled.UnalignedHeaderCell>
								<TableHeaderCell scope="col">Age</TableHeaderCell>
								<TableHeaderCell
									scope="col"
									data-id={
										planProfileTestIds.memberMySuperAccountBalTableHeader
									}
								>
									MLC MySuper Account
									<br />
									as at {asFormattedDate(balanceAsOfDate)}
								</TableHeaderCell>
								<TableHeaderCell
									scope="col"
									data-id={planProfileTestIds.memberAccountBalTableHeader}
								>
									Account Balance
									<br />
									as at {asFormattedDate(balanceAsOfDate)}
								</TableHeaderCell>
								<TableHeaderCell scope="col">Death Insurance</TableHeaderCell>
								<TableHeaderCell scope="col">TPD Insurance</TableHeaderCell>
								<TableHeaderCell scope="col">
									Income Protection
									<br />
									Monthly Benefit
								</TableHeaderCell>
								<TableHeaderCell scope="col">
									Insurance Commission
								</TableHeaderCell>
							</Styled.NoWrapTR>
						</TableHead>
						<MemberBenefitsTable memberBenefits={members} />
					</ScrollableTable>
				)}
			</Paginator>
			<ZeroMargin>
				<AEMContent
					variant="disclaimer"
					path="/secure/_content/Adviser/plan-profile/member-benefits/disclaimer"
				/>
			</ZeroMargin>
		</Tab>
	);
};

const MemberBenefitsTable = ({ memberBenefits }: { memberBenefits: any[] }) => {
	return (
		<TableBody data-id={planProfileTestIds.memberBenefitsTable}>
			{memberBenefits.length > 0 ? (
				memberBenefits.map((member, idx) => (
					<Styled.NoWrapTR
						key={idx}
						data-id={planProfileTestIds.memberBenefitsDetails}
					>
						<Styled.UnalignedCell>
							<div data-id={planProfileTestIds.memberInvestorNameLink}>
								{isMemberBlocked(member.investorName) ? (
									member.investorName
								) : (
									<TextLink
										target="_blank"
										fontWeight="semibold"
										href={`/online/adviser/clientprofile/${member.customerNumber}`}
									>
										{member.investorName}
									</TextLink>
								)}
							</div>
							<div data-id={planProfileTestIds.memberAccountNumber}>
								{member.accountNumber}
							</div>
						</Styled.UnalignedCell>
						<TableCell data-id={planProfileTestIds.memberAge}>
							{member.age}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberMySuperBalance}>
							{member.mySuperAccountBalance}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberAccountBal}>
							{member.accountBalance}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberDeathInsurance}>
							{member.deathInsurance}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberTpdInsurance}>
							{member.tpdInsurance}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberIncomeProtection}>
							{member.incomeProtectionMonthlyBenefit}
						</TableCell>
						<TableCell data-id={planProfileTestIds.memberInsuranceComm}>
							{member.insuranceCommission}
						</TableCell>
					</Styled.NoWrapTR>
				))
			) : (
				<TableRow>
					<TableCell colspan="100%">No matching records found</TableCell>
				</TableRow>
			)}
		</TableBody>
	);
};

const isMemberBlocked = (name: string) => MemberBlocked === name;

const applyFormatting = (benefits: MemberBenefitsItem[]) =>
	benefits.map((item: MemberBenefitsItem) => {
		const isBlocked = isMemberBlocked(item.surname);
		return {
			customerNumber: item.customerNumber,
			investorName: isBlocked
				? item.surname
				: item.surname.concat(', ', item.givenName),
			accountNumber: item.accountNumber,
			age: item.age,
			mySuperAccountBalance: asCurrency(item.mySuperAccountBalance, ''),
			accountBalance: asCurrency(item.accountBalance, ''),
			deathInsurance: asCurrency(item.deathInsurance, ''),
			tpdInsurance: asCurrency(item.tpdInsurance, ''),
			incomeProtectionMonthlyBenefit: asCurrency(
				item.incomeProtectionMonthlyBenefit,
				''
			),
			insuranceCommission: isBlocked
				? asPercentage(item.insuranceCommission)
				: asPercentage(item.insuranceCommission, true, 'N/A'),
		};
	});

const CsvFields = [
	{ name: 'investorName' },
	{ name: 'accountNumber' },
	{ name: 'age' },
	{
		name: 'mySuperAccountBalance',
		header: 'MLC MySuper Account Balance',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'accountBalance',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'deathInsurance',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'tpdInsurance',
		header: 'TPD Insurance',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'incomeProtectionMonthlyBenefit',
		apply: unicodeMinusToMinus,
	},
	{
		name: 'insuranceCommission',
		apply: unicodeMinusToMinus,
	},
];

const SearchCol = styled(Col)`
	padding-left: 0px;
`;

export default MemberBenefits;
